import { Fragment } from 'react';
import PropTypes from 'prop-types';

import { resolveScopedStyles } from 'core/utils/styled-jsx';
import modelPropTypes, { aboutAttributes } from 'core/utils/prop-types/model';
import resolveRelationships from 'core/utils/relationships';
import withTheme from 'core/hocs/withTheme';
import themePropTypes from 'core/utils/prop-types/theme';

import H1 from 'core/components/H1';
import H2 from 'core/components/H2';
import Page from 'core/components/Page';
import MarkdownWrapper from 'core/components/MarkdownWrapper';
import PageLoader from 'core/components/Loader/PageLoader';
import Divider from 'core/components/Divider';

import Agenda from './Agenda';
import Redaction from './Redaction';
import Contacts from './Contacts';
import LegalInfo from './LegalInfo';
import CaptionGroup from './Wrappers/CaptionGroup';
import Link from 'core/components/Link';

import styles from './index.styl';

const relationships = resolveRelationships(['authors'], {});

/* eslint-disable react/prop-types */
/* eslint-disable react/prop-types */
export const markdownRenderers = {
  a: ({ children, href }) => <Link to={href} target='_blank'>{children}</Link>,
};

/**
 * @author Aleksey Dibrivniy <a.dibrivniy@rambler-co.ru>
 */
function About(props) {
  const {
    content,
    logo,
    mapSrc,
    disableMap,
    isLoading,
    theme: {
      controls: {
        about: aboutAtoms,
      },
    },
  } = props;

  const {
    about,
    contributors,
    email,
    phone,
    phone_additional_code: phoneAdditionalCode,
    address_title: addressTitle,
    address_text: addressText,
    cooperation_text: cooperationText,
    cooperation_title: cooperationTitle,
    advertisement,
    registration_certificate_title: registrationCertificateTitle,
    registration_certificate_text: registrationCertificateText,
    terms_of_use_title: termsOfUseTitle,
    terms_of_use_text: termsOfUseText,
    founder_title: founderTitle,
    founder_text: founderText,
    meta_title: metaTitle,
    meta_description: metaDescription,
  } = content?.attributes || {};

  const isContacts = phone || phoneAdditionalCode || email || addressTitle || addressText || cooperationTitle || cooperationText;
  const isLegalInfo = registrationCertificateTitle || registrationCertificateText || termsOfUseTitle || termsOfUseText || founderTitle || founderText;

  const { authors } = relationships(content);

  const scope = resolveScopedStyles(
    <scope>
      <style jsx>{`
        .${styles.about}
          padding ${aboutAtoms.padding}

        .title
          margin 30px 0
          font ${aboutAtoms.headline.font}

        .subtitle
          margin 60px 0 30px
          font ${aboutAtoms.title2.font}
          color ${aboutAtoms.title2.color}

          :global(.mobile) &
            margin 30px 0
      `}</style>
    </scope>
  );

  return (
    <Page title={metaTitle || 'О проекте'} description={metaDescription}>
      <div className={scope.wrapClassNames(styles.about)}>
        {isLoading && <PageLoader />}

        {!isLoading && (
          <>
            {about && (
              <div className={styles.aboutProject}>
                <H1 className={scope.wrapClassNames('title')} dataQa='page-title'>О проекте</H1>
                <Agenda about={about} logo={logo} />
              </div>
            )}

            {(authors && authors.length > 0) && (
              <Fragment>
                <H2
                  className={scope.wrapClassNames('subtitle')}
                  id='edition'
                  dataQa='page-subtitle'
                >
                  Редакция
                </H2>
                <Redaction authors={authors} />
              </Fragment>
            )}

            {contributors && (
              <Fragment>
                <H2
                  className={scope.wrapClassNames('subtitle')}
                  id='contributors'
                  dataQa='page-subtitle'
                >
                  Контрибьюторы
                </H2>
                <MarkdownWrapper components={markdownRenderers}>
                  {contributors}
                </MarkdownWrapper>
              </Fragment>
            )}

            <Divider className={scope.wrapClassNames('divider')} />

            {isContacts && (
              <Fragment>
                <H2
                  className={scope.wrapClassNames('subtitle')}
                  id='contacts'
                  dataQa='page-subtitle'
                >
                  Контактная информация
                </H2>
                <Contacts
                  phone={phone}
                  phone_additional_code={phoneAdditionalCode}
                  email={email}
                  blocks={[
                    [addressTitle, addressText],
                    [cooperationTitle, cooperationText],
                  ]}
                />
              </Fragment>
            )}

            {!disableMap &&
              <iframe
                src={mapSrc}
                width='100%'
                height='400'
                frameBorder='0'
                allowFullScreen
              />
            }

            {advertisement && (
              <div className={styles.ads}>
                <H2
                  className={scope.wrapClassNames('subtitle')}
                  id='advertising'
                  dataQa='page-subtitle'
                >
                  Описание деятельности и реклама
                </H2>
                <CaptionGroup content={advertisement} />
              </div>
            )}

            <Divider className={scope.wrapClassNames('divider')} />

            {isLegalInfo && (
              <Fragment>
                <H2
                  className={scope.wrapClassNames('subtitle')}
                  id='legal'
                  dataQa='page-subtitle'
                >
                  Правовая информация
                </H2>
                <LegalInfo
                  blocks={[
                    [registrationCertificateTitle, registrationCertificateText],
                    [termsOfUseTitle, termsOfUseText],
                    [founderTitle, founderText],
                  ]}
                />
              </Fragment>
            )}
            <scope.styles />
          </>
        )}
      </div>
    </Page>
  );
}

About.propTypes = {
  /**
   * На некоторых проектах, например WMJ выводится лого.
   */
  logo: PropTypes.node,

  /**
   * Данные из API для построения страницы About.
   */
  content: modelPropTypes(aboutAttributes),

  /**
   * Сгенерированный src для iframe google maps.
   *
   * https://developers.google.com/maps/documentation/embed/quickstart
   */
  mapSrc: PropTypes.string,

  /**
   * Флаг позволяющий не выводить карту
   */
  disableMap: PropTypes.bool,

  /** Признак загрузки данных */
  isLoading: PropTypes.bool,

  theme: themePropTypes(`{
      controls: {
        about: {
          title2: {
            font,
            color,
          },
          headline: {
            font,
          },
          padding,
        },
      },
    }`),
};

About.defaultProps = {
  mapSrc: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.271053099265!2d37.623305345009115!3d55.69893742775758!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0xf6bde2714e10304e!2sRambler%26Co!5e0!3m2!1sru!2sru!4v1530533269975',
  customLoader: PageLoader,
};

export { About as StorybookComponent };
export default withTheme(About);
